import React, { createContext, useContext, useMemo, useState } from 'react';
import type { U3207InitLU } from './U3207Service';
import type { U3207BreadcrumbItem } from './utils/u3207Breadcrumb';

export interface U3207ContextType {
  initLU: U3207InitLU;
  month: string;
  setMonth: React.Dispatch<React.SetStateAction<string>>;
  providerId: string;
  setProviderId: React.Dispatch<React.SetStateAction<string>>;
  groupId: string;
  setGroupId: React.Dispatch<React.SetStateAction<string>>;
  drillTrail: U3207BreadcrumbItem[];
  setDrillTrail: React.Dispatch<React.SetStateAction<U3207BreadcrumbItem[]>>;
  drillOpen: boolean;
  setDrillOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

const U3207Context = createContext<U3207ContextType | undefined>(undefined);

interface U3207ContextProviderProps {
  initLU: U3207InitLU;
  children: React.ReactNode;
}

export const U3207ContextProvider: React.FC<U3207ContextProviderProps> = ({ initLU, children }) => {
  const [month, setMonth] = useState(initLU.defaultMonth);
  const [providerId, setProviderId] = useState('');
  const [groupId, setGroupId] = useState('');
  const [drillTrail, setDrillTrail] = useState<U3207BreadcrumbItem[]>([]);
  const [drillOpen, setDrillOpen] = useState(false);

  const value = useMemo(
    () => ({
      initLU,
      month,
      setMonth,
      providerId,
      setProviderId,
      groupId,
      setGroupId,
      drillTrail,
      setDrillTrail,
      drillOpen,
      setDrillOpen,
    }),
    [initLU, month, providerId, groupId, drillTrail, drillOpen],
  );

  return <U3207Context.Provider value={value}>{children}</U3207Context.Provider>;
};

export function useU3207Context(): U3207ContextType {
  const ctx = useContext(U3207Context);
  if (!ctx) {
    throw new Error('useU3207Context must be used within U3207ContextProvider');
  }
  return ctx;
}
